import type { routes } from '@semios/app-platform-banyan-route-definitions'
import { getEnumFromValueType, type TEmitterType } from '@semios/app-platform-values-helpers-and-types'
import type { TGroupedTableSection } from 'App/dashboards/_components/DashboardGroupedTable'
import {
  getApplicationRateFromMetadata,
  type TApplicationRate,
  type TFlowRateMetadata,
} from 'App/dashboards/_utils/applicationRate'
import { compareLabelsNullsLast } from 'App/dashboards/_utils/compareLabelsNullsLast'
import { getZoneApplicationRateByEmitterType } from 'App/dashboards/_utils/getZoneApplicationRateByEmitterType'
import { getZonesByEmitterType, type TPressureSensorZones } from 'App/dashboards/_utils/getZonesByEmitterType'
import { isPressureSensorValueType } from 'App/dashboards/_utils/isPressureSensorValueType'
import { isNil } from 'lodash'
import type moment from 'moment-timezone'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { fieldAssetStore } from 'stores/fieldAssetStore/fieldAssetStore'
import type { TPoint } from 'stores/fieldAssetStore/helperTypes'
import { apiFetch } from 'utils/apiFetch'
import { getIsoWithoutTheZ } from 'utils/getIsoWithoutTheZ'

// One (device, emitter-type) pair = one row. Volume (mm) + duration (ms) summed over the window.
export type TPressureSensorRow = {
  lngLat: string
  zoneIds: number[]
  zoneName: string | null
  emitterType: TEmitterType
  deviceName: string
  externalVendorNames: TPoint['externalVendorNames']
  applicationRate: TApplicationRate | null
  volumeMm: number | null
  durationMs: number | null
}

type TPressureDevice = {
  lngLat: string
  deviceName: string
  externalVendorNames: TPoint['externalVendorNames']
  // The applied-volume value types this device advertises (one per emitter type).
  valueTypes: string[]
  zonesByEmitterType: Partial<Record<TEmitterType, TPressureSensorZones>>
  zoneRateByEmitterType: Partial<Record<TEmitterType, TApplicationRate>>
}

type TPropertyPressure = {
  propertyId: number
  propertyName: string
  devices: TPressureDevice[]
  // Union of all advertised applied-volume value types — drives the request.
  valueTypes: Set<string>
}

const getPropertyPressure = (
  properties: NonNullable<ReturnType<typeof fieldAssetStore.getState>['properties']>,
  propertyIds: number[],
): TPropertyPressure[] =>
  propertyIds
    .map((propertyId) => {
      const property = properties[propertyId]

      if (!property) return null

      const devices = Object.values(property.points ?? {})
        .map((point) => ({
          lngLat: String(point.lngLat),
          deviceName: point.name,
          externalVendorNames: point.externalVendorNames,
          valueTypes: (point.valuesTimeseries ?? [])
            .filter(isPressureSensorValueType)
            .map((vt) => String(vt)),
          zonesByEmitterType: getZonesByEmitterType(property, point),
          zoneRateByEmitterType: getZoneApplicationRateByEmitterType(property, point),
        }))
        .filter((d) => d.valueTypes.length > 0)

      // Every selected property gets a section even with no pressure sensors (empty rows → a
      // per-property "no data" message), so the property isn't silently dropped from the widget.
      return {
        propertyId,
        propertyName: property.propertyName,
        devices,
        valueTypes: new Set(devices.flatMap((d) => d.valueTypes)),
      }
    })
    .filter((p) => p !== null)

export type TPressureSensorActivityResult = {
  sections: TGroupedTableSection<TPressureSensorRow>[]
  loading: boolean
  error: Error | null
  lastFetchedAt: Date | null
  refresh: () => void
}

// Fetches applied irrigation volume + on-duration at POINTS scope for every selected property's
// pressure-sensor devices (points advertising irrigationVolumeDurationSemios_*) in one /values call,
// then sums each (device, emitter-type) series over the window. Mirrors the Hub's
// pressureSensorsActivity summary (its apiArgsForPressureSensors request + content read).
export const usePressureSensorActivity = ({
  propertyIds,
  dateFrom,
  dateTo,
  refreshToken = 0,
}: {
  propertyIds: number[]
  dateFrom: moment.Moment
  dateTo: moment.Moment
  refreshToken?: number
}): TPressureSensorActivityResult => {
  const properties = fieldAssetStore.useSelector((s) => s.properties)
  const [data, setData] = useState<routes.Values.Response | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const [lastFetchedAt, setLastFetchedAt] = useState<Date | null>(null)
  const [refreshNonce, setRefreshNonce] = useState(0)
  const refresh = useCallback(() => setRefreshNonce((n) => n + 1), [])
  const dateFromMs = +dateFrom
  const dateToMs = +dateTo
  // Content key so a fresh propertyIds array each render doesn't churn the memo (and the fetch).
  const propertyIdsKey = propertyIds.join('|')

  const propertyPressure = useMemo(
    () => (properties ? getPropertyPressure(properties, propertyIds) : []),
    [properties, propertyIdsKey],
  )

  useEffect(() => {
    const lngLats = new Set<string>()
    const valuesRequested: Record<string, { preferredAggregationInterval: 'DAILY' }> = {}

    propertyPressure.forEach((p) => {
      p.devices.forEach((d) => lngLats.add(d.lngLat))

      p.valueTypes.forEach((vt) => (valuesRequested[vt] = { preferredAggregationInterval: 'DAILY' }))
    })

    if (lngLats.size === 0) {
      setData(null)

      setLoading(false)

      setError(null)

      return
    }

    let cancelled = false

    setLoading(true)

    setError(null)

    const body = {
      dateFrom: getIsoWithoutTheZ(dateFrom),
      dateTo: getIsoWithoutTheZ(dateTo),
      // Tag the call's source (as pest, Data Export and Property Playback do) so dashboard-driven
      // /values load is attributable in request logging. A declared optional enum on the Values
      // request schema (value-type-definitions/valuesTimeseriesTypes) that the API validates but
      // never consumes — so an invalid value 422s rather than being silently ignored.
      featureReferer: 'DASHBOARD',
      points: { lngLats: [...lngLats], valuesRequested },
    } as routes.Values.Request

    apiFetch({ url: '/values', body })
      .then((res) => {
        if (cancelled) return

        setData(res)

        setLastFetchedAt(new Date())
      })
      .catch((err) => {
        if (cancelled) return

        setError(err instanceof Error ? err : new Error(String(err)))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [propertyPressure, dateFromMs, dateToMs, refreshNonce, refreshToken])

  const sections = useMemo<TGroupedTableSection<TPressureSensorRow>[]>(() => {
    return propertyPressure.map((p) => ({
      propertyId: p.propertyId,
      propertyName: p.propertyName,
      rows: p.devices
        .flatMap((device) =>
          device.valueTypes.flatMap((valueType) => {
            const emitterType = getEnumFromValueType(valueType)

            if (!emitterType) return []

            const key = valueType as keyof NonNullable<
              NonNullable<routes.Values.Response['points']>[string]
            >['values']

            const series = data?.points?.[device.lngLat]?.values?.[key]?.[0]

            const timeseries = (series?.timeseries ?? []) as {
              value: { duration: number | null; volume_mm: number | null } | null
            }[]

            let volumeMm: number | null = null
            let durationMs: number | null = null

            timeseries.forEach((entry) => {
              const value = entry?.value

              if (!isNil(value?.volume_mm)) volumeMm = (volumeMm ?? 0) + (value?.volume_mm ?? 0)

              if (!isNil(value?.duration)) durationMs = (durationMs ?? 0) + (value?.duration ?? 0)
            })

            return [
              {
                lngLat: device.lngLat,
                zoneIds: device.zonesByEmitterType[emitterType]?.zoneIds ?? [],
                zoneName: device.zonesByEmitterType[emitterType]?.zoneName ?? null,
                emitterType,
                deviceName: device.deviceName,
                externalVendorNames: device.externalVendorNames,
                applicationRate: series
                  ? getApplicationRateFromMetadata(series.metadata as TFlowRateMetadata | undefined)
                  : (device.zoneRateByEmitterType[emitterType] ?? null),
                volumeMm,
                durationMs,
              },
            ]
          }),
        )
        // Sorted by zone, then emitter type, then device, so the Zone group column collapses contiguously.
        .sort(
          (a, b) =>
            compareLabelsNullsLast(a.zoneName, b.zoneName) ||
            a.emitterType.localeCompare(b.emitterType) ||
            a.deviceName.localeCompare(b.deviceName),
        ),
    }))
  }, [propertyPressure, data])

  return useMemo(
    () => ({ sections, loading, error, lastFetchedAt, refresh }),
    [sections, loading, error, lastFetchedAt, refresh],
  )
}
